import { BoxGeometry, Group, Mesh } from 'three';
import type { FacadeSide, GarageConfig, HouseBodyConfig } from '@/model/schema';
import type { MaterialKit } from '../materials';
import type { HingeDef } from '../hinges';
import type { HingeCollector } from './buildWalls';
import { bakeFrameWorldUVs } from '../uv';

/**
 * Build a BoxGeometry, then bake world-aligned UVs against the mesh's
 * position in its parent group so the wall texture tiles continuously
 * with adjacent exterior-wall slabs (see `uv.ts`).
 */
function makeWallBox(
  sx: number,
  sy: number,
  sz: number,
  cx: number,
  cy: number,
  cz: number,
): BoxGeometry {
  const geo = new BoxGeometry(sx, sy, sz);
  bakeFrameWorldUVs(geo, cx, cy, cz);
  return geo;
}

/**
 * Attached garage builder.
 *
 * Local frame (matches buildWalls.ts §"Facade-local frame"):
 *   local +X = along the host facade, 0 at the lower-index corner
 *   local +Y = up
 *   local +Z = INWARD toward the host facade (so OUTWARD is local -Z).
 *              The garage box therefore lives at z ∈ [-outward, 0].
 *
 * Two orientations:
 *
 *  - sideFacing (legacy): garage door is on the OUTWARD short face
 *    (local z = -depth). Box: width along the host facade × depth
 *    outward. Used for rear-loaded / alley-style garages.
 *
 *  - frontFacing (preferred): garage door is on the FRONT-facing
 *    short face (perpendicular to the host facade). Local +X is along
 *    the host facade with x=0 at the lower-index corner; the FRONT
 *    short face is the local x=0 plane on the right facade and the
 *    local x=depth plane on the left facade. The garage extends
 *    outward by `width` (= car width), and along the host facade by
 *    `depth` (= parking-bay length).
 */

const ROOF_THICKNESS = 0.12;
const WALL_THICKNESS = 0.15;
/** Garage floor: kept very thin so the car effectively rolls onto an
 *  at-grade slab, not up a 15 cm step. The slab top sits exactly at
 *  y=0 (grade); its bottom sits 2 cm below grade and so quietly
 *  overlaps the lot base plate without raising the entry. The user
 *  flagged the previous 15 cm slab as unrealistic. */
const FLOOR_THICKNESS = 0.02;
/** Tiny lift above grade so the garage floor doesn't share a plane with
 *  the lot base plate top (z-fighting / flicker user-reported). 1 cm is
 *  invisible but enough to break the coplanar surface. */
const FLOOR_LIFT = 0.01;
const ROOF_OVERHANG = 0.2;

export function buildGarage(
  body: HouseBodyConfig,
  garage: GarageConfig,
  kit: MaterialKit,
  hinges: HingeCollector,
): Group {
  const group = new Group();
  group.name = 'Garage';

  const local = new Group();
  local.name = 'GarageShell';

  if (garage.orientation === 'frontFacing') {
    buildFrontFacingShell(local, garage, kit, hinges);
  } else {
    buildSideFacingShell(local, garage, kit, hinges);
  }

  applySideTransform(local, body, garage);
  group.add(local);
  return group;
}

/* --------------------------- sideFacing ------------------------------ */

function buildSideFacingShell(
  local: Group,
  garage: GarageConfig,
  kit: MaterialKit,
  hinges: HingeCollector,
): void {
  const w = garage.width;
  const d = garage.depth;
  const h = garage.height;

  const floor = new Mesh(new BoxGeometry(w, FLOOR_THICKNESS, d), kit.driveway);
  floor.name = 'FloorSlab';
  floor.receiveShadow = true;
  // Lift slightly above grade so we don't z-fight the lot base plate.
  floor.position.set(w / 2, FLOOR_LIFT - FLOOR_THICKNESS / 2, -d / 2);
  local.add(floor);

  {
    const cx = WALL_THICKNESS / 2;
    const cy = h / 2;
    const cz = -d / 2;
    const sideWallLeft = new Mesh(makeWallBox(WALL_THICKNESS, h, d, cx, cy, cz), kit.exteriorWall);
    sideWallLeft.name = 'WallLeft';
    sideWallLeft.castShadow = true;
    sideWallLeft.receiveShadow = true;
    sideWallLeft.position.set(cx, cy, cz);
    local.add(sideWallLeft);
  }

  {
    const cx = w - WALL_THICKNESS / 2;
    const cy = h / 2;
    const cz = -d / 2;
    const sideWallRight = new Mesh(makeWallBox(WALL_THICKNESS, h, d, cx, cy, cz), kit.exteriorWall);
    sideWallRight.name = 'WallRight';
    sideWallRight.castShadow = true;
    sideWallRight.receiveShadow = true;
    sideWallRight.position.set(cx, cy, cz);
    local.add(sideWallRight);
  }

  const doorW = Math.max(0.8, w - 0.6);
  const doorH = Math.min(h - 0.25, 2.3);
  const jambW = (w - doorW) / 2;
  {
    const cx = (jambW + WALL_THICKNESS) / 2 - WALL_THICKNESS;
    const cy = h / 2;
    const cz = -d + WALL_THICKNESS / 2;
    const jambLeft = new Mesh(
      makeWallBox(jambW + WALL_THICKNESS, h, WALL_THICKNESS, cx, cy, cz),
      kit.exteriorWall,
    );
    jambLeft.name = 'WallFar_jamb_left';
    jambLeft.castShadow = true;
    jambLeft.receiveShadow = true;
    jambLeft.position.set(cx, cy, cz);
    local.add(jambLeft);
  }

  {
    const cx = w - (jambW + WALL_THICKNESS) / 2 + WALL_THICKNESS;
    const cy = h / 2;
    const cz = -d + WALL_THICKNESS / 2;
    const jambRight = new Mesh(
      makeWallBox(jambW + WALL_THICKNESS, h, WALL_THICKNESS, cx, cy, cz),
      kit.exteriorWall,
    );
    jambRight.name = 'WallFar_jamb_right';
    jambRight.castShadow = true;
    jambRight.receiveShadow = true;
    jambRight.position.set(cx, cy, cz);
    local.add(jambRight);
  }

  const headerH = h - doorH;
  {
    const cx = w / 2;
    const cy = doorH + headerH / 2;
    const cz = -d + WALL_THICKNESS / 2;
    const header = new Mesh(
      makeWallBox(doorW, headerH, WALL_THICKNESS, cx, cy, cz),
      kit.exteriorWall,
    );
    header.name = 'WallFar_header';
    header.castShadow = true;
    header.receiveShadow = true;
    header.position.set(cx, cy, cz);
    local.add(header);
  }

  // Garage roof: overhang on three outward sides (left, right, outer),
  // but FLUSH on the host-facade side (z=0) so we don't poke 0.4 m of
  // roof into the body's wall — that was the user-reported "garage
  // cuts into the house" symptom.
  const roofD = d + ROOF_OVERHANG;
  const roof = new Mesh(
    new BoxGeometry(w + 2 * ROOF_OVERHANG, ROOF_THICKNESS, roofD),
    kit.roof,
  );
  roof.name = 'GarageRoof';
  roof.castShadow = true;
  roof.receiveShadow = true;
  roof.position.set(w / 2, h + ROOF_THICKNESS / 2, -d / 2 - ROOF_OVERHANG / 2);
  local.add(roof);

  const doorContainer = new Group();
  doorContainer.name = `Door_${garage.side}-garage-panel`;
  local.add(doorContainer);

  const pivot = new Group();
  pivot.name = `Hinge_garage-${garage.side}`;
  pivot.position.set(w / 2, doorH, -d - 0.002);
  const panelT = 0.04;
  const panel = new Mesh(new BoxGeometry(doorW - 0.02, doorH - 0.02, panelT), kit.door);
  panel.name = 'Panel';
  panel.castShadow = true;
  panel.receiveShadow = true;
  panel.position.set(0, -(doorH - 0.02) / 2, -panelT / 2);
  pivot.add(panel);

  // Tilt-up: hinge axis is the local X axis (along the host facade).
  // R_x(+90°) lifts the panel bottom OUTWARD (+90 about X maps the
  // hanging -Y vector onto -Z, which is the "outside" of the garage
  // for a sideFacing door). User asked for outward-swinging doors as
  // the default — without this the panel rolled INTO the garage.
  const def: HingeDef = {
    id: `garage-${garage.side}`,
    label: `Garage Door · ${sideTitle(garage.side)}`,
    category: 'exterior-door',
    axis: 'X',
    minDeg: 0,
    maxDeg: 90,
    openSignFromMin: 0,
  };
  pivot.userData.hinge = def;
  hinges.add(def);
  doorContainer.add(pivot);
}

/* --------------------------- frontFacing ----------------------------- */

function buildFrontFacingShell(
  local: Group,
  garage: GarageConfig,
  kit: MaterialKit,
  hinges: HingeCollector,
): void {
  // Garage extends OUTWARD by `garage.width` (lateral, perpendicular
  // to host facade) and ALONG the host facade by `garage.depth`.
  // OUTWARD = local -Z direction (per applySideTransform).
  const d = garage.depth; // along host facade (local +X)
  const w = garage.width; // outward extent (local -Z)
  const h = garage.height;
  // Door is on the "front" short face. With local +X running from the
  // facade's lower-index corner toward its higher-index corner, the
  // FRONT short face is at local x=0 for the RIGHT facade (where +X
  // points back) and at local x=d for the LEFT facade (where +X points
  // toward the front).
  const doorAtX0 = garage.side === 'right' || garage.side === 'front';
  const doorPlaneX = doorAtX0 ? 0 : d;

  const floor = new Mesh(new BoxGeometry(d, FLOOR_THICKNESS, w), kit.driveway);
  floor.name = 'FloorSlab';
  floor.receiveShadow = true;
  // Lift slightly above grade so we don't z-fight the lot base plate.
  floor.position.set(d / 2, FLOOR_LIFT - FLOOR_THICKNESS / 2, -w / 2);
  local.add(floor);

  // Long outer wall (parallel to host facade, opposite the host wall).
  {
    const cx = d / 2;
    const cy = h / 2;
    const cz = -w + WALL_THICKNESS / 2;
    const longOuter = new Mesh(makeWallBox(d, h, WALL_THICKNESS, cx, cy, cz), kit.exteriorWall);
    longOuter.name = 'WallOuter';
    longOuter.castShadow = true;
    longOuter.receiveShadow = true;
    longOuter.position.set(cx, cy, cz);
    local.add(longOuter);
  }

  // Short BACK wall (no door).
  {
    const cx = doorAtX0 ? d - WALL_THICKNESS / 2 : WALL_THICKNESS / 2;
    const cy = h / 2;
    const cz = -w / 2;
    const backWall = new Mesh(makeWallBox(WALL_THICKNESS, h, w, cx, cy, cz), kit.exteriorWall);
    backWall.name = 'WallBack';
    backWall.castShadow = true;
    backWall.receiveShadow = true;
    backWall.position.set(cx, cy, cz);
    local.add(backWall);
  }

  // Short FRONT wall with garage-door cutout: two jambs (one on the
  // outer side, one on the host-side) plus a header.
  const doorW = Math.max(0.8, w - 0.6);
  const doorH = Math.min(h - 0.25, 2.3);
  const jambW = (w - doorW) / 2;
  const jambZ = (zCenter: number, name: string) => {
    const cx = doorPlaneX;
    const cy = h / 2;
    const cz = zCenter;
    const m = new Mesh(makeWallBox(WALL_THICKNESS, h, jambW, cx, cy, cz), kit.exteriorWall);
    m.name = name;
    m.castShadow = true;
    m.receiveShadow = true;
    m.position.set(cx, cy, cz);
    local.add(m);
  };
  jambZ(-jambW / 2, 'WallFront_jamb_inner');
  jambZ(-w + jambW / 2, 'WallFront_jamb_outer');
  const headerH = h - doorH;
  {
    const cx = doorPlaneX;
    const cy = doorH + headerH / 2;
    const cz = -w / 2;
    const header = new Mesh(
      makeWallBox(WALL_THICKNESS, headerH, doorW, cx, cy, cz),
      kit.exteriorWall,
    );
    header.name = 'WallFront_header';
    header.castShadow = true;
    header.receiveShadow = true;
    header.position.set(cx, cy, cz);
    local.add(header);
  }

  // Garage roof: overhang on three outward sides (front, back, outer)
  // but FLUSH on the host-facade side (z=0). Earlier the roof box was
  // sized w + 2*ROOF_OVERHANG and centred so it poked 0.4 m INTO the
  // body, producing the visual "garage cuts the house" gap reported
  // in image 1 of the latest pass.
  const roofW = w + ROOF_OVERHANG;
  const roof = new Mesh(
    new BoxGeometry(d + 2 * ROOF_OVERHANG, ROOF_THICKNESS, roofW),
    kit.roof,
  );
  roof.name = 'GarageRoof';
  roof.castShadow = true;
  roof.receiveShadow = true;
  roof.position.set(d / 2, h + ROOF_THICKNESS / 2, -w / 2 - ROOF_OVERHANG / 2);
  local.add(roof);

  // Roll-up door pivot. Pivot sits at the TOP center of the opening
  // on the OUTSIDE face of the door plane. "Outside" is x<0 for
  // doorAtX0 (right facade — garage door faces front, beyond x=0) and
  // x>d for left facade.
  const pivotX = doorAtX0 ? -0.002 : d + 0.002;
  const doorContainer = new Group();
  doorContainer.name = `Door_${garage.side}-garage-panel`;
  local.add(doorContainer);

  const pivot = new Group();
  pivot.name = `Hinge_garage-${garage.side}`;
  pivot.position.set(pivotX, doorH, -w / 2);
  const panelT = 0.04;
  const panel = new Mesh(new BoxGeometry(panelT, doorH - 0.02, doorW - 0.02), kit.door);
  panel.name = 'Panel';
  panel.castShadow = true;
  panel.receiveShadow = true;
  panel.position.set(doorAtX0 ? -panelT / 2 : panelT / 2, -(doorH - 0.02) / 2, 0);
  pivot.add(panel);

  // Hinge axis = local Z (the lateral span of the door, perpendicular
  // to the host facade). The pivot sits at the TOP of the door
  // opening, on the OUTSIDE face of the door plane. We rotate the
  // panel about Z so its bottom edge swings UP and OUTWARD (away from
  // the garage interior) — a tilt-up motion. Direction depends on
  // which side of the body the garage sits on:
  //   - doorAtX0 (right facade): door opening is at local x=0, pivot
  //     just outside at x≈-0.002. R_z(-90°) maps (0, -doorH/2) onto
  //     (-doorH/2, 0), which is OUTWARD (-X). So open = -90°.
  //   - !doorAtX0 (left facade): pivot at x≈d+0.002. R_z(+90°) maps
  //     (0, -doorH/2) onto (+doorH/2, 0), OUTWARD relative to the
  //     left-side garage. So open = +90°.
  // Both orientations are CLOSED at 0° (panel hanging straight down).
  const def: HingeDef = {
    id: `garage-${garage.side}`,
    label: `Garage Door · ${sideTitle(garage.side)} (front)`,
    category: 'exterior-door',
    axis: 'Z',
    minDeg: doorAtX0 ? -90 : 0,
    maxDeg: doorAtX0 ? 0 : 90,
    openSignFromMin: doorAtX0 ? 1 : 0,
  };
  pivot.userData.hinge = def;
  hinges.add(def);
  doorContainer.add(pivot);
}

/* --------------------------- transform ------------------------------- */

function applySideTransform(
  local: Group,
  body: HouseBodyConfig,
  garage: GarageConfig,
): void {
  const { side, offset } = garage;
  switch (side) {
    case 'front':
      local.position.set(body.originX + offset, 0, body.originZ);
      local.rotation.y = 0;
      break;
    case 'right':
      local.position.set(
        body.originX + body.footprintWidth,
        0,
        body.originZ + offset,
      );
      local.rotation.y = -Math.PI / 2;
      break;
    case 'back':
      local.position.set(
        body.originX + body.footprintWidth - offset,
        0,
        body.originZ + body.footprintDepth,
      );
      local.rotation.y = Math.PI;
      break;
    case 'left':
      local.position.set(
        body.originX,
        0,
        body.originZ + body.footprintDepth - offset,
      );
      local.rotation.y = Math.PI / 2;
      break;
  }
}

function sideTitle(side: FacadeSide): string {
  return side[0]!.toUpperCase() + side.slice(1);
}
